import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, addMinutes } from "date-fns";
import { UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { useCreateAppointment, useCheckDoctorConflict } from "@/hooks/useAppointments";
import { useDoctorsByClinic } from "@/hooks/useDoctorsByClinic";
import { useAppointmentTypes } from "@/hooks/useAppointmentTypes";
import { PhoneNumberPatientSearch } from "./PhoneNumberPatientSearch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { appointmentSchema } from "@/lib/validations";
import { toast } from "@/hooks/use-toast";
import { DoctorDaySchedulePanel } from "./DoctorDaySchedulePanel";
import { AvailableSlotsGrid } from "./AvailableSlotsGrid";
import { supabase } from "@/integrations/supabase/client";
import { QuickCreatePatientDialog } from "@/components/patients/QuickCreatePatientDialog";


type AppointmentFormData = z.infer<typeof appointmentSchema>;

interface CreateAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  defaultDate?: Date;
  defaultOperatory?: string;
  preSelectedPatient?: {
    id: string;
    name: string;
    phone?: string;
  };
  onAppointmentCreated?: (appointmentId: string) => void;
  defaultPhone?: string;
  defaultDoctorId?: string;
  defaultReason?: string;
  defaultIsFollowUp?: boolean;
  defaultParentVisitId?: string | null;
}

export const CreateAppointmentDialog = ({
  open,
  onOpenChange,
  clinicId,
  defaultDate,
  preSelectedPatient,
  onAppointmentCreated,
  defaultPhone,
  defaultDoctorId,
  defaultReason,
  defaultIsFollowUp = false,
  defaultParentVisitId = null,
}: CreateAppointmentDialogProps) => {
  const [selectedPatient, setSelectedPatient] = useState<{
    id: string;
    name: string;
    phone: string;
  } | null>(preSelectedPatient ? { ...preSelectedPatient, phone: preSelectedPatient.phone || "" } : null);
  const [selectedClinicName, setSelectedClinicName] = useState<string>("");
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: doctors = [] } = useDoctorsByClinic(clinicId);
  const { data: appointmentTypes = [] } = useAppointmentTypes(clinicId);
  const createAppointment = useCreateAppointment();
  const checkDoctorConflict = useCheckDoctorConflict();

  const form = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      patient_id: "",
      patient_name: "",
      patient_phone: "",
      doctor_id: "",
      appointment_type_id: "",
      date: defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      start_time: defaultDate ? format(defaultDate, "HH:mm") : "09:00",
      duration_minutes: 30,
      reason_for_visit: "",
      notes: "",
      status: "scheduled",
    },
  });

  useEffect(() => {
    if (open) {
      const baseDate = defaultDate ?? new Date();
      
      // If we have a pre-selected patient, use their info
      const patientId = preSelectedPatient?.id || "";
      const patientName = preSelectedPatient?.name || "";
      const patientPhone = preSelectedPatient?.phone || "";
      
      form.reset({
        patient_id: patientId,
        patient_name: patientName,
        patient_phone: patientPhone || defaultPhone || "",
        doctor_id: defaultDoctorId || "",
        appointment_type_id: "",
        date: format(baseDate, "yyyy-MM-dd"),
        start_time: format(baseDate, "HH:mm"),
        duration_minutes: 30,
        reason_for_visit: defaultReason || "",
        notes: "",
        status: "scheduled",
      });
      
      // Set selected patient state if pre-selected
      if (preSelectedPatient) {
        setSelectedPatient({
          id: preSelectedPatient.id,
          name: preSelectedPatient.name,
          phone: preSelectedPatient.phone || "",
        });
      } else {
        setSelectedPatient(null);
      }
      
      setSelectedSlot(null);
      
      // Fetch clinic name for display
      if (clinicId) {
        supabase
          .from("clinics")
          .select("name")
          .eq("id", clinicId)
          .single()
          .then(({ data }) => {
            if (data) setSelectedClinicName(data.name);
          });
      }
    }
  }, [open, defaultDate, form, clinicId, preSelectedPatient, defaultPhone, defaultDoctorId, defaultReason]);

  const handlePatientSelect = (patient: typeof selectedPatient) => {
    setSelectedPatient(patient);
    if (patient) {
      form.setValue("patient_id", patient.id);
      form.setValue("patient_name", patient.name);
      form.setValue("patient_phone", patient.phone);
    } else {
      form.setValue("patient_id", "");
      form.setValue("patient_name", "");
    }
  };

  const handleSlotSelect = (startTime: string, endTime: string) => {
    const formattedStartTime = startTime.slice(0, 5); // HH:MM format
    form.setValue("start_time", formattedStartTime);
    
    // Calculate duration in minutes
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    form.setValue("duration_minutes", durationMinutes);
    
    // Track selected slot for visual feedback
    setSelectedSlot({ startTime: formattedStartTime, endTime: endTime.slice(0, 5) });
  };

  const onSubmit = async (data: AppointmentFormData) => {
    // Prevent double submission
    if (isSubmitting) return;
    
    // Validate that a slot was selected from the grid
    if (!selectedSlot) {
      toast({
        title: "Time Slot Required",
        description: "Please select an available time slot from the grid",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const [year, month, day] = data.date.split("-").map(Number);
      const [hour, minute] = data.start_time.split(":").map(Number);

      const startDateTime = new Date(year, month - 1, day, hour, minute, 0, 0);
      const endDateTime = addMinutes(startDateTime, data.duration_minutes);

      const startISOString = startDateTime.toISOString();
      const endISOString = endDateTime.toISOString();

      // Check for doctor conflicts
      const conflicts = await checkDoctorConflict.mutateAsync({
        doctorId: data.doctor_id,
        startTime: startISOString,
        endTime: endISOString,
      });

      if (conflicts && conflicts.length > 0) {
        const conflict = conflicts[0];
        const conflictStart = format(new Date(conflict.start_time), "HH:mm");
        const conflictEnd = format(new Date(conflict.end_time), "HH:mm");
        
        toast({
          title: "Doctor Not Available",
          description: `This doctor already has an appointment at ${conflict.clinic_name} from ${conflictStart} to ${conflictEnd}`,
          variant: "destructive",
        });
        return;
      }

      const createdAppointment = await createAppointment.mutateAsync({
        clinic_id: clinicId,
        patient_id: data.patient_id || null,
        patient_name: data.patient_name,
        patient_phone: data.patient_phone || null,
        doctor_id: data.doctor_id,
        operatory_id: null,
        appointment_type_id: data.appointment_type_id || null,
        start_time: startISOString,
        end_time: endISOString,
        duration_minutes: data.duration_minutes,
        status: data.status,
        title: null,
        cancelled_at: null,
        cancelled_by: null,
        cancellation_reason: null,
        reminder_sent_at: null,
        confirmation_sent_at: null,
        reason_for_visit: data.reason_for_visit || null,
        notes: data.notes || null,
        parent_visit_id: defaultParentVisitId,
        is_follow_up: defaultIsFollowUp,
      });

      toast({
        title: "Appointment Created",
        description: `Appointment scheduled for ${data.patient_name}`,
      });


      if (createdAppointment?.id && onAppointmentCreated) {
        onAppointmentCreated(createdAppointment.id);
      }

      form.reset();
      setSelectedPatient(null);
      setSelectedSlot(null);
      onOpenChange(false);
    } catch (error: any) {
      console.error("[CreateAppointmentDialog] Error:", error);
      toast({
        title: "Failed to create appointment",
        description: error?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log("[CreateAppointmentDialog] Validation errors:", errors);
    
    // Show specific error messages
    if (errors.patient_id) {
      toast({
        title: "Patient Required",
        description: "Please select an existing patient or create a new one",
        variant: "destructive",
      });
    } else if (errors.doctor_id) {
      toast({
        title: "Doctor Required",
        description: "Please select a doctor for this appointment",
        variant: "destructive",
      });
    } else if (errors.date || errors.start_time) {
      toast({
        title: "Date/Time Required",
        description: "Please select a valid date and time",
        variant: "destructive",
      });
    } else {
      // Generic validation error
      const firstError = Object.values(errors)[0] as any;
      toast({
        title: "Validation Error",
        description: firstError?.message || "Please fill in all required fields",
        variant: "destructive",
      });
    }
  };

  const handlePatientCreated = async (patientId: string) => {
    // Fetch the newly created patient's details
    const { data: patient } = await supabase
      .from("patients")
      .select("id, name, phone")
      .eq("id", patientId)
      .single();

    if (patient) {
      setSelectedPatient({
        id: patient.id,
        name: patient.name || "",
        phone: patient.phone || "",
      });
      form.setValue("patient_id", patient.id);
      form.setValue("patient_name", patient.name || "");
      form.setValue("patient_phone", patient.phone || "");
    }
    setShowCreatePatient(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{defaultIsFollowUp ? "Schedule Follow-up" : "Create Appointment"}</DialogTitle>
          <DialogDescription>
            {defaultIsFollowUp
              ? "Schedule a follow-up visit for a patient."
              : "Schedule a new appointment for a patient."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-4">
            <div className="space-y-4">
              {/* Phone-based Patient Search */}
              <PhoneNumberPatientSearch
                value={form.watch("patient_phone")}
                onPhoneChange={(phone) => form.setValue("patient_phone", phone)}
                onPatientSelect={handlePatientSelect}
                selectedPatient={selectedPatient}
              />

              {/* Inline Patient Creation - Show when no patient found */}
              {!selectedPatient && form.watch("patient_phone") && form.watch("patient_phone").length >= 3 && (
                <div className="p-3 rounded-lg border border-dashed border-primary/50 bg-primary/5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">
                        Patient not found? Create a new patient record first.
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCreatePatient(true)}
                      className="shrink-0"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Patient
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="doctor_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Doctor *</FormLabel>
                      <Select onValueChange={(value) => {
                        field.onChange(value);
                        // Clear selected slot when doctor changes
                        setSelectedSlot(null);
                      }} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select doctor" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {doctors.length === 0 ? (
                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                              No doctors assigned to this clinic.<br />
                              Please configure doctor schedules in Admin Settings.
                            </div>
                          ) : (
                            doctors.map((doc) => (
                              <SelectItem key={doc.id} value={doc.id}>
                                {doc.full_name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="appointment_type_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Appointment Type</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          const selectedType = appointmentTypes.find(t => t.id === value);
                          if (selectedType?.default_duration_minutes) {
                            form.setValue("duration_minutes", selectedType.default_duration_minutes);
                          }
                        }}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type (optional)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {appointmentTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name} {type.default_duration_minutes && `(${type.default_duration_minutes} min)`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Multi-Clinic Doctor Schedule Sections */}
              {form.watch("doctor_id") && form.watch("date") && (
                <div className="space-y-4">
                  <DoctorDaySchedulePanel
                    doctorId={form.watch("doctor_id")}
                    date={form.watch("date") ? new Date(form.watch("date")) : undefined}
                  />
                  
                  <AvailableSlotsGrid
                    doctorId={form.watch("doctor_id")}
                    clinicId={clinicId}
                    date={form.watch("date") ? new Date(form.watch("date")) : undefined}
                    clinicName={selectedClinicName}
                    onSlotSelect={handleSlotSelect}
                    selectedSlot={selectedSlot?.startTime}
                  />
                  
                  {/* Selected Slot Display */}
                  {selectedSlot && (
                    <div className="p-3 rounded-lg border border-primary bg-primary/5">
                      <p className="text-sm font-medium text-primary">
                        Selected Time: {selectedSlot.startTime} - {selectedSlot.endTime}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date *</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                            // Clear selected slot when date changes
                            setSelectedSlot(null);
                          }}
                          placeholder="Select date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (min)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="5"
                          step="5"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          disabled={!!selectedSlot}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              {/* Slot Selection Prompt */}
              {form.watch("doctor_id") && form.watch("date") && !selectedSlot && (
                <p className="text-sm text-muted-foreground">
                  ↑ Please select an available time slot from the grid above
                </p>
              )}

              <FormField
                control={form.control}
                name="reason_for_visit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason for Visit (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="E.g., Regular checkup, toothache, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Add any additional notes..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || createAppointment.isPending}
                className="w-full"
              >
                {isSubmitting ? "Creating..." : "Create Appointment"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {/* Inline Quick Patient Creation Dialog */}
      <QuickCreatePatientDialog
        open={showCreatePatient}
        onOpenChange={setShowCreatePatient}
        onSuccess={handlePatientCreated}
        clinicId={clinicId}
        defaultPhone={form.watch("patient_phone")}
      />

    </Dialog>
  );
};
